import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';

import { useAuthStore } from '@/store/auth.store';
import { notificationsService } from '@/modules/notifications/services';
import { InAppNotification } from '@/modules/notifications/types';
import { readCookieToken, ENTITY_ROUTES } from '@/modules/notifications/utils/notifications.utils';

export function useNotifications() {
  const navigate = useNavigate();
  const hydrated = useAuthStore(state => state.hydrated);
  const token = useAuthStore(state => state.token);
  const fallbackCookieToken = readCookieToken();
  const effectiveToken = token ?? fallbackCookieToken;

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const isOpenRef = useRef(false);

  const hasUnread = unreadCount > 0;
  const unreadInListCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    if (!token && fallbackCookieToken) {
      useAuthStore.setState({ token: fallbackCookieToken });
    }
  }, [token, fallbackCookieToken]);

  const applyUnreadCount = useCallback((nextUnreadCount: number, notify = true) => {
    setUnreadCount(previousUnreadCount => {
      if (notify && nextUnreadCount > previousUnreadCount) {
        const delta = nextUnreadCount - previousUnreadCount;
        toast.success(
          delta === 1 ? 'Tienes 1 aviso nuevo sin leer' : `Tienes ${delta} avisos nuevos sin leer`,
        );
      }
      return nextUnreadCount;
    });
  }, []);

  const loadCounters = useCallback(async () => {
    if (!effectiveToken) {
      return;
    }

    try {
      const counters = await notificationsService.getCounters();
      if (counters) {
        applyUnreadCount(counters.unreadCount, false);
      }
    } catch {
      // Silent on purpose. Notifications should not block navigation.
    }
  }, [effectiveToken, applyUnreadCount]);

  const loadNotifications = useCallback(async () => {
    if (!effectiveToken) {
      return;
    }

    setLoading(true);
    try {
      const page = await notificationsService.getFeed(0, 12, false);
      if (page) {
        setNotifications(page.content);
      }
    } catch {
      // Silent on purpose.
    } finally {
      setLoading(false);
    }
  }, [effectiveToken]);

  useEffect(() => {
    isOpenRef.current = open;
  }, [open]);

  // WebSocket connection
  useEffect(() => {
    if (!effectiveToken) {
      return;
    }

    let reconnectTimerId: number | null = null;
    let disconnect: (() => void) | null = null;
    let cancelled = false;

    const connect = () => {
      disconnect = notificationsService.connectWebSocket(effectiveToken, {
        onEvent: event => {
          if (event.type === 'UNREAD_COUNT_CHANGED') {
            applyUnreadCount(event.unreadCount);
          } else {
            setUnreadCount(event.unreadCount);
          }

          if (isOpenRef.current) {
            void loadNotifications();
          }
        },
        onError: () => {
          if (cancelled) {
            return;
          }
          reconnectTimerId = window.setTimeout(() => {
            connect();
          }, 3_000);
        },
      });
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimerId) {
        window.clearTimeout(reconnectTimerId);
      }
      disconnect?.();
    };
  }, [effectiveToken, loadNotifications, applyUnreadCount]);

  // Initial counters load
  useEffect(() => {
    if (!effectiveToken) {
      setUnreadCount(0);
      setNotifications([]);
      return;
    }

    void loadCounters();
  }, [effectiveToken, loadCounters, hydrated]);

  const handleOpenChange = useCallback(
    async (nextOpen: boolean) => {
      setOpen(nextOpen);

      if (nextOpen) {
        await Promise.all([loadCounters(), loadNotifications()]);
      }
    },
    [loadCounters, loadNotifications],
  );

  const markReadOptimistic = (id: string, readAt: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true, readAt } : notification,
      ),
    );
  };

  const handleNotificationClick = async (notification: InAppNotification) => {
    if (!notification.read) {
      try {
        const counters = await notificationsService.markRead(notification.id);
        if (counters) {
          setUnreadCount(counters.unreadCount);
        }
        markReadOptimistic(notification.id, new Date().toISOString());
      } catch {
        // Silent on purpose.
      }
    }

    const entityId = notification.entityId ?? '';
    const entityType = notification.entityType ?? undefined;
    if (entityType && ENTITY_ROUTES[entityType]) {
      navigate(ENTITY_ROUTES[entityType](entityId));
      setOpen(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    const prevNotifications = notifications;
    const prevUnreadCount = unreadCount;

    const readAt = new Date().toISOString();
    setNotifications(prev => prev.map(notification => ({ ...notification, read: true, readAt })));
    setUnreadCount(0);
    setMarkingAll(true);

    try {
      const response = await notificationsService.markAllRead();
      if (response) {
        setUnreadCount(response.unreadCount);
      }
    } catch {
      setNotifications(prevNotifications);
      setUnreadCount(prevUnreadCount);
    } finally {
      setMarkingAll(false);
    }
  };

  const buttonLabel = useMemo(() => {
    if (unreadCount <= 0) {
      return 'Sin notificaciones nuevas';
    }
    if (unreadCount === 1) {
      return '1 notificación sin leer';
    }
    return `${unreadCount} notificaciones sin leer`;
  }, [unreadCount]);

  return {
    open,
    loading,
    markingAll,
    unreadCount,
    notifications,
    hasUnread,
    unreadInListCount,
    buttonLabel,
    handleOpenChange,
    handleNotificationClick,
    handleMarkAllAsRead,
  };
}
